/**
 * Formik-bound, assignments-aware flavour of `RuleConditionsQueryBuilder`.
 * Sibling of `Communications/QueryBuilder/CommunicationConditionsQueryBuilderFormik`
 * — same skeleton (read `values[fieldId]`, gate on `submitCount`, compute
 * prereqs, call a registry hook, fire `onFieldsLoaded`, render the pure QB)
 * but parameterised over a different first-prereq dim (`entity` vs `eventKey`),
 * a different org-context source (`useSysconfig` vs `useOrganization`), and a
 * different catalog endpoint (assignments legacy vs rules_engine framework).
 *
 * The two wrappers are intentionally kept side-by-side: the shared
 * `useConditionFieldsFetcher` already factors out the catalog-fetch race/digest
 * plumbing, and the remaining ~30 lines of duplication vary along 4 axes that
 * don't compose cleanly into one component. When assignments migrates onto the
 * rules_engine endpoint (registering its own `rule_set_type`), the two will
 * collapse mechanically into a single Formik wrapper.
 *
 * Reads `values[fieldId]` and writes via `setFieldValue`. Gates the controls'
 * "Required" state on `submitCount > 0`. Fetches the field registry via
 * `useConditionFieldRegistry` (assignments endpoint) and surfaces it to the
 * parent through `onFieldsLoaded` so the form-level Yup/`validate` prop can
 * walk the tree against the same registry. Computes the "select an LOB / sub-
 * org" prerequisite message and the "no fields for this LOB" empty message
 * here — the pure component is rendering-only and knows nothing about scope.
 */

import React, { useEffect } from 'react';
import type { RuleGroupType } from 'react-querybuilder';
import { getIn, useFormikContext } from 'formik';

import { useSysconfig } from '../../../SystemConfigurationScreen';

import RuleConditionsQueryBuilder from './RuleConditionsQueryBuilder';
import type { FieldDefinitionDto } from './types';
import { useConditionFieldRegistry } from './useConditionFieldRegistry';

interface RuleConditionsQueryBuilderFormikProps {
  /** Formik path holding the `conditions_json` tree (default: `'conditions_json'`). */
  fieldId?: string;
  /** Selects which providers contribute fields. Empty string = "not yet picked"
   *  — the fetch is gated until the user selects an entity (the BE requires it
   *  since `condition_fields` is entity-aware). */
  entity: 'claims' | 'exposures' | '';
  lob: string;
  isAllSubOrgs: boolean;
  subOrgIds: number[];
  /** Lets the parent form mirror the registry into its own state for use in
   *  Formik's form-level `validate` prop. */
  onFieldsLoaded?: (fields: FieldDefinitionDto[]) => void;
  /** Whether the catalog fetch is in flight, for a parent that gates on it.
   *
   *  Both this and `onFieldsLoaded` report the same fetch, and a parent that validates against
   *  the registry needs both: mid-flight the registry is `[]`, which is indistinguishable from
   *  an organization that genuinely has no fields for this scope. */
  onLoadingChange?: (isLoading: boolean) => void;
}

interface PrerequisiteCheck {
  /** True when entity + LOB + (sub-org-set OR all-sub-orgs OR sub-orgs-disabled) all hold. */
  prerequisitesMet: boolean;
  /** Copy shown in place of the QB while prerequisites aren't met; `null` when met. */
  prerequisiteMessage: string | null;
}

/** Compute the assignments-specific prerequisite predicate + placeholder copy.
 *  All three scope dimensions must be set before the catalog fetch is fired:
 *  the BE catalog endpoint requires `entity` and `lob`; sub-org-enabled orgs
 *  also need an explicit sub-org pick (or the all-sub-orgs flag). */
const checkPrerequisites = (
  entity: string,
  lob: string,
  isAllSubOrgs: boolean,
  subOrgIds: number[],
  subOrgsEnabled: boolean
): PrerequisiteCheck => {
  const hasOrgScope = !subOrgsEnabled || isAllSubOrgs || (subOrgIds && subOrgIds.length > 0);
  if (entity && lob && hasOrgScope) {
    return { prerequisitesMet: true, prerequisiteMessage: null };
  }
  // Reverse order so the message reads as the natural form-fill order: entity
  // is picked first (drives whether the rule attaches to a claim or an
  // exposure), then LOB, then sub-org.
  const subOrgPart = subOrgsEnabled ? ', and Sub-Organization' : '';
  return {
    prerequisitesMet: false,
    prerequisiteMessage: `Select an Entity, a Line of Business${subOrgPart} to configure conditions.`,
  };
};

const RuleConditionsQueryBuilderFormik: React.FC<RuleConditionsQueryBuilderFormikProps> = ({
  fieldId = 'conditions_json',
  entity,
  lob,
  isAllSubOrgs,
  subOrgIds,
  onFieldsLoaded,
  onLoadingChange,
}) => {
  const { values, setFieldValue, submitCount } = useFormikContext();
  const value = (getIn(values, fieldId) as RuleGroupType | null | undefined) ?? null;

  // useSysconfig is exported from a JSX file without types; cast at the boundary.
  const { organization } = useSysconfig() as {
    organization: { id: number; sub_organizations_enabled?: boolean };
  };

  const { prerequisitesMet, prerequisiteMessage } = checkPrerequisites(
    entity,
    lob,
    isAllSubOrgs,
    subOrgIds,
    !!organization.sub_organizations_enabled
  );

  const { fields, isLoading } = useConditionFieldRegistry({
    organizationId: organization.id,
    enabled: prerequisitesMet,
    // Coerce the `''` sentinel away — the hook ignores params when
    // `enabled` is false, so the assertion is safe.
    entity: (entity || 'claims') as 'claims' | 'exposures',
    lob,
    isAllSubOrgs,
    subOrgIds,
  });

  // Intentionally only depends on `fields` — `onFieldsLoaded` identity is
  // irrelevant. Including it would silently infinite-loop if a future caller
  // passes a non-memoized inline arrow function.
  useEffect(() => {
    onFieldsLoaded?.(fields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields]);

  // Same deps discipline as `onFieldsLoaded` above: depend on the value, not on the callback
  // identity, so an inline arrow from the parent cannot loop.
  useEffect(() => {
    onLoadingChange?.(isLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  return (
    <RuleConditionsQueryBuilder
      value={value}
      onChange={(q) => setFieldValue(fieldId, q)}
      shouldShowErrors={submitCount > 0}
      fields={fields}
      isLoading={isLoading}
      prerequisiteMessage={prerequisiteMessage}
      emptyMessage="No condition fields available for this LOB."
    />
  );
};

export default RuleConditionsQueryBuilderFormik;
